// components/PreviewHeader.tsx
import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../../middleware/ThemeContext';
import { TempSettings, MenuItem } from '../../types/types';
import { Theme } from '../../utils/themes';

const HeaderContainer = styled.header<{
  theme: Theme;
  $backgroundColor?: string;
  $layout?: 'left' | 'right' | 'center';
}>`
  background: ${({ $backgroundColor }) => $backgroundColor || 'white'};
  padding: 20px 30px;
  display: flex;
  justify-content: ${({ $layout }) => $layout === 'center' ? 'center' : 'space-between'};
  align-items: center;
  flex-direction: ${({ $layout }) => ($layout === 'center' ? 'column' : $layout === 'right' ? 'row-reverse' : 'row')};
  gap: ${({ $layout }) => ($layout === 'center' ? '12px' : '0')};
  box-shadow: 0px 4px 40px 0px #0000001A;
  border-radius: 10px 10px 0px 0px;
`;

const Navigation = styled.nav<{ $textColor?: string; $layout?: 'left' | 'right' | 'center'; $primaryFont?: string }>`
  display: flex;
  gap: 30px;
  justify-content: ${({ $layout }) => ($layout === 'center' ? 'center' : 'flex-start')};
  width: ${({ $layout }) => ($layout === 'center' ? '100%' : 'auto')};
  
  a {
    color: ${({ $textColor }) => $textColor || '#495057'};
    text-decoration: none;
    font-size: 17px;
    font-weight: 600;
    transition: color 0.2s;
    font-family: ${({ $primaryFont }) => $primaryFont || 'Raleway, sans-serif'};
    &:hover {
      color: #6c63ff;
    }
  }
`;

const NavItem = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownMenu = styled.div<{ $textColor?: string; $backgroundColor?: string }>`
  position: absolute;
  top: 100%;
  left: 0;
  background: ${({ $backgroundColor }) => $backgroundColor || 'white'};
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 8px 0;
  min-width: 200px;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px);
  transition: all 0.3s ease;
  z-index: 1000;
  margin-top: 8px;

  ${NavItem}:hover & {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }

  a {
    display: block;
    padding: 10px 20px;
    color: ${({ $textColor }) => $textColor || '#495057'};
    text-decoration: none;
    font-size: 15px;
    font-weight: 500;
    transition: background 0.2s;
    
    &:hover {
      background: #f8f9fa;
      color: #6c63ff;
    }
  }
`;

interface PreviewHeaderProps {
  settings: TempSettings;
  menuItems?: MenuItem[];
  primaryFont?: string;
}

const PreviewHeader: React.FC<PreviewHeaderProps> = ({ settings, menuItems = [], primaryFont }) => {
  const { theme } = useTheme();
  
  const rawHeaderAlignment = settings.headerAlignment || 'left-right';
  const headerAlignment = (
    rawHeaderAlignment === 'left-right'
      ? 'left'
      : rawHeaderAlignment === 'right-left'
        ? 'right'
        : rawHeaderAlignment === 'center-stacked'
          ? 'center'
          : rawHeaderAlignment
  ) as 'left' | 'right' | 'center';
  
  const headerTextColor = settings.textColor || theme.CorporateTheme.colors.headerColor;
  const headerBackgroundColor = settings.backgroundColor || 'white';

  return (
    <HeaderContainer 
      theme={theme}
      $layout={headerAlignment}
      $backgroundColor={headerBackgroundColor}
    >
      <div className='d-flex align-items-center gap-2'>
        <div>
          {settings.websiteLogo ? (
            <img
              src={settings.websiteLogo}
              alt="Website Logo"
              style={{ height: '40px', width: 'auto', objectFit: 'contain' }}
            />
          ) : null}
        </div>
      </div>
      <Navigation $textColor={headerTextColor} $layout={headerAlignment} $primaryFont={primaryFont}>
        {menuItems.length > 0 ? (
          menuItems.map((item) => (
            <NavItem key={item.id}>
              <a
                href={item.page.url}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                {item.page.name}
              </a>
              {item.children && item.children.length > 0 && (
                <DropdownMenu $textColor={headerTextColor} $backgroundColor={headerBackgroundColor}>
                  {item.children.map((child) => (
                    <a
                      key={child.id}
                      href={child.page.url}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      {child.page.name}
                    </a>
                  ))}
                </DropdownMenu>
              )}
            </NavItem>
          ))
        ) : (
          <>
            <a href="#home" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>Home</a>
            <a href="#about" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>About Us</a>
            <a href="#contact" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>Contact Us</a>
          </>
        )}
      </Navigation>
    </HeaderContainer>
  );
};

export default PreviewHeader;
